import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListMusic, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, mediaUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Reveal } from "./Reveal";
import { Waveform } from "./Waveform";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

export const CountdownBanner = () => {
  const [cfg, setCfg] = useState(null);
  const [item, setItem] = useState(null);
  const [previewTrack, setPreviewTrack] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const { playTrack, track: current, playing } = usePlayer();

  const join = async () => {
    if (!email.trim()) return;
    setJoining(true);
    try {
      await api.post("/subscribe", { email });
      setJoined(true);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setJoining(false);
    }
  };

  useEffect(() => {
    Promise.all([api.get("/settings/public"), api.get("/releases"), api.get("/albums")])
      .then(([s, r, a]) => {
        const cd = s.data?.countdown;
        if (!cd?.enabled || !cd.release_id || !cd.target) return;
        if (cd.release_id.startsWith("album:")) {
          const al = a.data.find((x) => x.id === cd.release_id.slice(6));
          if (!al) return;
          setItem(al);
          const first = r.data
            .filter((t) => t.album_id === al.id)
            .sort((x, y) => (x.order ?? 0) - (y.order ?? 0))[0];
          setPreviewTrack(first || null);
        } else {
          const t = r.data.find((x) => x.id === cd.release_id);
          if (!t) return;
          setItem(t);
          setPreviewTrack(t);
        }
        setCfg(cd);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!cfg) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [cfg]);

  if (!cfg || !item) return null;

  const diff = new Date(cfg.target).getTime() - now;
  const past = diff <= 0;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff / 3600000) % 24);
  const mins = Math.floor((diff / 60000) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  const active = previewTrack && current?.id === previewTrack.id;
  const units = [
    [days, "days"],
    [hours, "hrs"],
    [mins, "min"],
    [secs, "sec"],
  ];

  return (
    <section className="border-y border-gold-400/15 bg-navy-900/40" data-testid="countdown-banner">
      <Reveal>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-16 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8 md:gap-12 items-center">
          <div className="relative w-44 md:w-full mx-auto">
            <div className="absolute -inset-5 rounded-full bg-gold-400/15 blur-3xl" aria-hidden />
            <img
              src={mediaUrl(item.cover_url)}
              alt={`${item.title} cover art`}
              className="relative w-full aspect-square object-cover rounded-xl border border-gold-400/30"
              data-testid="countdown-cover"
            />
          </div>
          <div className="text-center md:text-left">
            <p className="eyebrow mb-2">{past ? "Out now" : "Coming soon"}</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-slate-50" data-testid="countdown-headline">
              {cfg.headline || item.title}
            </h2>
            {cfg.message && (
              <p className="mt-3 text-slate-300/85 font-light leading-relaxed max-w-xl mx-auto md:mx-0" data-testid="countdown-message">
                {cfg.message}
              </p>
            )}
            {past ? (
              <p className="mt-6 font-serif text-4xl sm:text-5xl text-gold-gradient tracking-wide" data-testid="countdown-out-now">
                OUT NOW
              </p>
            ) : (
              <div className="mt-6 flex justify-center md:justify-start gap-3 sm:gap-4" data-testid="countdown-timer">
                {units.map(([v, label]) => (
                  <div key={label} className="w-16 sm:w-20 rounded-xl border border-gold-400/25 bg-navy-950/60 py-3 text-center">
                    <p className="font-serif text-2xl sm:text-3xl text-gold-300 tabular-nums" data-testid={`countdown-${label}`}>
                      {pad(v)}
                    </p>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-1">{label}</p>
                  </div>
                ))}
              </div>
            )}
            {cfg.release_id.startsWith("album:") ? (
              <div className="mt-6 flex items-center justify-center md:justify-start gap-4">
                <Link
                  to={`/music?album=${item.id}`}
                  data-testid="countdown-album-link"
                  className="btn-ghost-gold !px-5 !py-2.5 text-xs inline-flex items-center gap-2"
                >
                  <ListMusic size={15} /> Preview all tracks
                </Link>
                <span className="text-[10px] tracking-[0.2em] uppercase text-gold-400/70">60-sec previews</span>
              </div>
            ) : previewTrack && (
              <div className="mt-6 flex items-center justify-center md:justify-start gap-4">
                <button
                  onClick={() => playTrack(previewTrack)}
                  data-testid="countdown-play"
                  aria-label={`Play ${previewTrack.title} preview`}
                  className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-navy-950 transition-transform hover:scale-105"
                  style={{ background: "linear-gradient(135deg,#F59E0B,#D4AF37 50%,#B38728)" }}
                >
                  {active && playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
                </button>
                <Waveform playing={active && playing} bars={24} className="h-9 w-32 sm:w-44" />
                <span className="text-[10px] tracking-[0.2em] uppercase text-gold-400/70">60-sec preview</span>
              </div>
            )}
            <div className="mt-6 max-w-md mx-auto md:mx-0" data-testid="countdown-notify">
              {joined ? (
                <p className="text-sm text-gold-300" data-testid="countdown-joined">
                  You're on the list — we'll email you the moment it drops.
                </p>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") join(); }}
                    placeholder="Your email — get the launch email"
                    className="flex-1 min-w-0 bg-navy-950/80 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-gold-400/60 transition-colors"
                    data-testid="countdown-email-input"
                  />
                  <button
                    onClick={join}
                    disabled={joining}
                    className="btn-gold !px-5 !py-2.5 text-xs shrink-0 disabled:opacity-50"
                    data-testid="countdown-notify-btn"
                  >
                    {joining ? "Joining…" : "Notify me"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
